import {AfterContentInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {MatAutocomplete} from '@angular/material/autocomplete';
import {AbstractContactModel} from '@app/models/abstract-contact.model';
import {CallLogEntryModel} from '@app/models/call-log-entry.model';
import {ContactModel} from '@app/models/contact.model';
import {PhonenumberEntry} from '@app/models/phonenumber-entry';
import {QueueAgentModel} from '@app/models/queue-data.model';
import {GtAttendantService} from '@app/services/gt-attendant.service';
import {GtCallControlService} from '@app/services/gt-call-control.service';
import {GtDirectoryService} from '@app/services/gt-directory.service';
import {GtManageOrganizationService} from '@app/services/gt-manage-organization.service';
import {GtNotificationService} from '@app/services/gt-notification.service';
import {GtPhoneNumberUtil} from '@app/shared/utils/gt-phone-number.util';
import {GtServiceNumbersUtil} from '@app/shared/utils/gt-service-numbers.util';
import {PhoneAndNameFormatUtil} from '@app/shared/utils/phone-and-name-format-util';
import {environment} from '@environment';
import {EndUser} from '@gt-api';
import * as moment from 'moment';
import {Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';

@Component({
  selector: 'gt-phone-number-selector',
  templateUrl: './phone-number-selector.component.html',
  styleUrls: ['./phone-number-selector.component.scss']
})
export class PhoneNumberSelectorComponent implements OnInit, OnChanges, AfterContentInit {
  @Input() oname: string;
  @Input() searchMainNumbers = false;
  @Input() searchAnswerGroup = false;
  @Input() searchMenus = false;
  @Input() searchEndUsers = false;
  @Input() searchContacts = false;
  @Input() searchContactGroups = false;
  @Input() searchEndUserCallLogs = false;
  @Input() searchMainNumberCallLogs = false;
  @Input() allowOtherNumber = false;
  @Input() allowShortNumber = false;
  @Input() floatPlaceholder = 'auto';
  @Input() initialValue = '';
  @Input() allowMultipleNums = false;
  @Input() shouldFocus = false;
  @Input() showLabel = true;
  @Input() label = 'phone_number_selector.input';
  @Input() canEdit = true;
  @Input() excludeAgentList: QueueAgentModel[] = [];
  @Input() excludeAgentLoginList: string[] = [];
  @Input() excludePhoneNumberList: string[] = [];

  @Output() phoneNumberSelected = new EventEmitter<PhonenumberEntry>();
  @Output() accepted = new EventEmitter<string>();

  @ViewChild('auto') autoComplete: MatAutocomplete;
  @ViewChild('input') inputEl: ElementRef;
  listOfNums: PhonenumberEntry[];

  private rawTextString;

  private currentValue = new PhonenumberEntry();
  private allContacts: Array<AbstractContactModel> = [];
  private serviceNumbers: Array<AbstractContactModel> = [];
  private contactList: Array<AbstractContactModel> = [];
  private endUserCalls: Array<AbstractContactModel> = [];

  private mainNumberCalls: Array<AbstractContactModel> = [];
  public filteredOptions: Observable<Array<AbstractContactModel>>;

  public inputControl = new UntypedFormControl();

  constructor(
    private manageOrganizationService: GtManageOrganizationService,
    private notificationService: GtNotificationService,
    private directoryService: GtDirectoryService,
    private callLogService: GtCallControlService,
    private mainNumberCallLogService: GtAttendantService
  ) {}

  ngOnInit() {
    if (this.canEdit) {
      this.ngOnChanges();
    }
    this.updateInputControlValue(this.initialValue);
    if (!this.canEdit && this.initialValue && this.initialValue !== '') {
      this.inputControl.disable();
    }
    this.listOfNums = [];

    this.filteredOptions = this.inputControl.valueChanges.pipe(
      startWith(null as string),
      map(text => (text ? this.filterOptions(text) : this.allContacts.slice()))
    );
  }

  ngOnChanges() {
    const oname = this.oname ? this.oname : this.manageOrganizationService.manageOname;
    this.serviceNumbers = [];
    this.contactList = [];
    this.updateContactList();

    if (this.searchContacts || this.searchEndUsers) {
      this.directoryService.getContacts(oname, false).subscribe(contactList => {
        this.contactList = contactList.filter(
          c => (this.searchContacts && !(c as EndUser).login) || (this.searchEndUsers && (c as EndUser).login)
        );
        this.updateContactList();
      }, this.error);
    }

    if (this.searchMainNumbers || this.searchAnswerGroup || this.searchMenus) {
      let patternToSearch = '1';
      if (this.searchAnswerGroup) {
        patternToSearch = environment.answerGroupPattern;
      } else if (this.searchMenus) {
        patternToSearch = environment.menuPattern;
      }
      this.directoryService.getServiceNumberModels(oname, false).subscribe(snds => {
        this.serviceNumbers = snds.filter(snd => snd.pattern === patternToSearch);
        this.updateContactList();
      }, this.error);
    }

    if (this.searchEndUserCallLogs) {
      this.callLogService.getEndUserCallLog().subscribe(entries => {
        this.endUserCalls = entries
          .map(ContactModel.convertEndUserCallLogEntry)
          .filter(c => c.phoneNumber && c.phoneNumber.length > 0) // remove those without number
          .filter((c, index, self) => self.findIndex(t => t.phoneNumber === c.phoneNumber) === index); // remove duplicates
        this.updateContactList();
      }, this.error);
    }

    if (this.searchMainNumberCallLogs) {
      this.mainNumberCalls = [];

      this.directoryService.getServiceNumbers().subscribe(serviceNumbers => {
        serviceNumbers.filter(GtServiceNumbersUtil.visibleInCallLog).forEach(s => {
          const from = moment().format('YYYY-MM--2');
          this.mainNumberCallLogService
            .getServiceNumberCallLog(s.number, undefined, undefined, from)
            .subscribe((entries: CallLogEntryModel[]) => {
              entries
                .map(entry => ContactModel.convertCallLogEntryModel(entry))
                .filter(c => c.phoneNumber && c.phoneNumber.length > 0) // remove those without number
                .filter((c, index, self) => self.findIndex(t => t.phoneNumber === c.phoneNumber) === index) // remove duplicates
                .forEach(c => this.mainNumberCalls.push(c));
              this.updateContactList();
            }, this.error);
        });
      });
    }
    // todo add contact groups
  }

  ngAfterContentInit() {
    if (this.shouldFocus) {
      setTimeout(() => {
        this.inputEl.nativeElement.focus();
      }, 500);
    }
  }

  updateInputControlValue(newValue: string) {
    this.inputControl.setValue(newValue);
  }

  valueChanged(text: string) {
    this.rawTextString = text;
    this.currentValue = new PhonenumberEntry();
    this.phoneNumberSelected.emit(this.currentValue);

    if (this.allowMultipleNums) {
      const nums = text.split(',');
      const listOfValidNums = nums
        .filter(n => {
          return GtPhoneNumberUtil.isValidNumber(n);
        })
        .map(n => {
          return new PhonenumberEntry(GtPhoneNumberUtil.toBackend(n));
        });
    } else {
      if (this.allowShortNumber) {
        const contact = this.contactList.find(c => c.login && c.shortNumber === text);
        if (contact) {
          this.currentValue = new PhonenumberEntry(contact.phoneNumber);
          console.log('valid short number: ', text, contact.phoneNumber);
        }
      }
      if (this.allowOtherNumber) {
        text = text.replace(/\D/g, '');
        if (GtPhoneNumberUtil.isValidNumber(text)) {
          this.currentValue = new PhonenumberEntry(GtPhoneNumberUtil.toBackend(text));
        }
      }
    }

    this.phoneNumberSelected.emit(this.currentValue);
  }

  updateContactList() {
    this.allContacts = []
      .concat(this.contactList)
      .concat(this.serviceNumbers)
      .concat(this.endUserCalls)
      .concat(this.mainNumberCalls)
      .filter((c, index, self) => self.findIndex(t => t.phoneNumber === c.phoneNumber) === index)
      .filter(c => this.excludeAgentList.findIndex(el => el.login === c.login) < 0) // removes those in exclude agent list
      .filter(c => this.excludeAgentLoginList.findIndex(el => el === c.login) < 0) // removes those in exclude agent login list
      .filter(c => this.excludePhoneNumberList.findIndex(num => num === c.phoneNumber) < 0) // removes those in exclude answer group list
      .sort((a, b) => (a?.name ? a.name.localeCompare(b?.name, moment.locale()) : -1));
  }

  private error = error => {
    this.notificationService.showErrorMessage(error);
  };

  filterOptions(text: string): Array<AbstractContactModel> {
    return this.allContacts.filter(contact => {
      return (
        (contact.name && contact.name.toLowerCase().includes(text.toLowerCase())) ||
        (contact.phoneNumber && GtPhoneNumberUtil.isSameNumber(text, contact.phoneNumber)) ||
        (contact.login && contact.shortNumber && contact.shortNumber.toLowerCase().includes(text.toLowerCase()))
      );
    });
  }

  setSelected(phoneNumber: string) {
    const contact = this.allContacts.find(c => c.phoneNumber === phoneNumber);
    this.currentValue = new PhonenumberEntry(GtPhoneNumberUtil.toBackend(contact.phoneNumber), contact.name, contact.login);
    this.phoneNumberSelected.emit(this.currentValue);

    this.inputControl.setValue(PhoneAndNameFormatUtil.toDisplay(contact.phoneNumber, contact.name));
    if (this.allowMultipleNums) {
      this.accepted.emit(phoneNumber);
    }
  }

  getRawString(): string {
    this.currentValue = null;
    return this.rawTextString;
  }

  acceptChoice() {
    if (this.currentValue.fullNumber && !this.autoComplete.isOpen) {
      this.accepted.emit();
    } else if (this.allowMultipleNums) {
      if (GtPhoneNumberUtil.isValidNumber(this.currentValue.fullNumber)) {
        this.phoneNumberSelected.emit(new PhonenumberEntry(this.currentValue.fullNumber));
      }
      this.accepted.emit(this.rawTextString);
    }
  }

  displayFn(phoneNumber?: string): string | undefined {
    if (this.allContacts === undefined) {
      return phoneNumber ? GtPhoneNumberUtil.toDisplay(phoneNumber) : undefined;
    }
    const contact = this.allContacts.find(c => c.phoneNumber === phoneNumber);
    return contact ? PhoneAndNameFormatUtil.toDisplay(phoneNumber, contact.name) : phoneNumber;
  }
}
